'use client'
import type React from 'react'
import { motion, type Variants } from 'framer-motion'
import { ServiceCard } from './ServiceCard'

const SERVICES = [
  {
    id: 'dry-van',
    title: 'Dry Van',
    description:
      'Standard enclosed trailers for general freight. Temperature-stable loads, palletized goods, and high-cube cargo moved safely and on schedule.',
  },
  {
    id: 'reefer',
    title: 'Reefer',
    description:
      'Temperature-controlled refrigerated loads. Pharmaceuticals, perishables, and cold-chain freight handled with precision monitoring.',
  },
  {
    id: 'power-only',
    title: 'Power Only',
    description:
      'Drop-and-hook efficiency. Our tractors move your pre-loaded trailers — ideal for drop yards, relay lanes, and high-volume shipper networks.',
  },
]

const svgProps = {
  width: 48,
  height: 48,
  viewBox: '0 0 40 40',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  className: 'text-[color:var(--accent)]',
}

const ICONS: Record<string, React.ReactElement> = {
  'dry-van': (
    <svg {...svgProps}>
      <rect x="2" y="7" width="34" height="20" rx="1.5" />
      <line x1="32" y1="8" x2="32" y2="26" />
      <circle cx="9" cy="30" r="3.5" />
      <circle cx="27" cy="30" r="3.5" />
      <line x1="12.5" y1="30" x2="23.5" y2="30" />
    </svg>
  ),
  reefer: (
    <svg {...svgProps}>
      <line x1="20" y1="4" x2="20" y2="36" />
      <line x1="33.9" y1="12" x2="6.1" y2="28" />
      <line x1="33.9" y1="28" x2="6.1" y2="12" />
      <circle cx="20" cy="20" r="2.5" fill="currentColor" stroke="none" />
      <line x1="17" y1="8" x2="20" y2="4" /><line x1="23" y1="8" x2="20" y2="4" />
      <line x1="17" y1="32" x2="20" y2="36" /><line x1="23" y1="32" x2="20" y2="36" />
    </svg>
  ),
  'power-only': (
    <svg {...svgProps}>
      <rect x="19" y="7" width="16" height="20" rx="1.5" />
      <line x1="20" y1="12" x2="31" y2="12" />
      <rect x="5" y="15" width="16" height="12" rx="1.5" />
      <line x1="32" y1="7" x2="32" y2="3" />
      <circle cx="30" cy="31" r="3.5" />
      <circle cx="10" cy="31" r="3.5" />
      <line x1="13.5" y1="31" x2="26.5" y2="31" />
    </svg>
  ),
}

const cardGridVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
}

export function ServicesSection() {
  return (
    <section id="services" className="relative overflow-hidden py-28">
      {/* Background: loading dock image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/images/services-dock.png)' }}
        aria-hidden
      />
      {/* Solid base so cards stay readable */}
      <div className="absolute inset-0 bg-[#0f172a]/75" />
      {/* Gradient: slightly lighter mid-section for the cinematic reveal feel */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.15) 45%, rgba(15,23,42,0.65) 100%)',
        }}
      />

      <div className="relative z-10 page-container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <p className="text-sm font-semibold tracking-widest uppercase text-[color:var(--accent)] opacity-80 mb-3">
            Equipment &amp; Capabilities
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
            What We Haul
          </h2>
          <p className="mt-4 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Three service modes. One accountable carrier.
          </p>
        </motion.div>

        <motion.ul
          className="services-grid grid grid-cols-1 md:grid-cols-3 gap-6 list-none p-0"
          variants={cardGridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {SERVICES.map(({ id, title, description }, index) => (
            <ServiceCard
              key={id}
              id={id}
              title={title}
              description={description}
              icon={ICONS[id]}
              index={index}
            />
          ))}
        </motion.ul>
      </div>
    </section>
  )
}
